import { useState, useEffect } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { EQUIP_STATUS, TEXT_FIELDS, DATE_FIELDS, emptyEquip } from "./equipmentMeta";
import { EquipmentPhotos, QueuedPhotos } from "./EquipmentPhotos";

const inputCls = "w-full px-3 py-2 border border-zinc-300 rounded-none text-sm focus:outline-none focus:ring-2 focus:ring-brand";
const numOrNull = (v) => (v === "" || v == null ? null : Number(v));

const Field = ({ label, children }) => (
  <div>
    <label className="text-sm font-bold text-zinc-900 block mb-1">{label}</label>
    {children}
  </div>
);

const Section = ({ title, children }) => (
  <div className="space-y-3">
    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-brand border-b border-zinc-200 pb-1">{title}</div>
    {children}
  </div>
);

const toForm = (e) => ({
  ...emptyEquip,
  ...Object.fromEntries(Object.keys(emptyEquip).map((k) => [k, e[k] ?? ""])),
  quantity: e.quantity,
});

export const EquipmentFormDialog = ({ open, onOpenChange, editing, locations, onSaved }) => {
  const [form, setForm] = useState(emptyEquip);
  const [saving, setSaving] = useState(false);
  const [live, setLive] = useState(null);
  const [queued, setQueued] = useState([]);
  useEffect(() => { if (open) { setForm(editing ? toForm(editing) : emptyEquip); setLive(editing); setQueued([]); } }, [open, editing]);

  const bind = (k) => ({ value: form[k], onChange: (e) => setForm({ ...form, [k]: e.target.value }) });
  const autoHire = editing && editing.reserved > 0;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(), category: form.category.trim(), location: form.location,
        description: form.description.trim(), quantity: Number(form.quantity), status: form.status,
        daily_rate: Number(form.daily_rate), weekly_rate: numOrNull(form.weekly_rate),
        monthly_rate: numOrNull(form.monthly_rate), replacement_value: numOrNull(form.replacement_value),
        ...Object.fromEntries(TEXT_FIELDS.map((f) => [f, (form[f] || "").trim()])),
      };
      if (editing) await api.put(`/equipment/${editing.id}`, payload);
      else {
        const { data } = await api.post("/equipment", payload);
        let failed = 0;
        for (const file of queued) {
          const fd = new FormData();
          fd.append("file", file);
          await api.post(`/equipment/${data.id}/photos`, fd).catch(() => { failed += 1; });
        }
        if (failed) toast.error(`${failed} photo(s) could not be uploaded`);
      }
      toast.success(editing ? "Equipment updated" : "Equipment added");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="equipment-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-black tracking-tighter">
            {editing ? "Edit Equipment" : "Add Equipment"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="space-y-6">
          <Section title="Photos (up to 3, first is main)">
            <div className="sm:col-span-2">
              {editing && live ? (
                <EquipmentPhotos equipment={live} canManage onChange={(u) => { setLive(u); onSaved(); }} />
              ) : (
                <QueuedPhotos files={queued} setFiles={setQueued} />
              )}
            </div>
          </Section>
          <Section title="Identification">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Asset ID / Equipment No."><input {...bind("asset_id")} data-testid="equipment-asset-id-input" className={inputCls} placeholder="e.g. JQ-0042" /></Field>
              <Field label="Name"><input required {...bind("name")} data-testid="equipment-name-input" className={inputCls} /></Field>
            </div>
            <Field label="Equipment description"><textarea {...bind("description")} data-testid="equipment-description-input" className={inputCls} rows={2} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Equipment category"><input {...bind("category")} data-testid="equipment-category-input" className={inputCls} placeholder="e.g. Cranes" /></Field>
              <Field label="Make and model"><input {...bind("make_model")} data-testid="equipment-make-model-input" className={inputCls} /></Field>
              <Field label="Serial number"><input {...bind("serial_number")} data-testid="equipment-serial-input" className={inputCls} /></Field>
              <Field label="WLL / Capacity / Rating"><input {...bind("wll")} data-testid="equipment-wll-input" className={inputCls} placeholder="e.g. 5t WLL" /></Field>
              <Field label="Weight and dimensions"><input {...bind("weight_dimensions")} data-testid="equipment-weight-input" className={inputCls} placeholder="e.g. 120kg, 1.2 x 0.8 x 0.6m" /></Field>
              <Field label="Asset owner (if externally owned)"><input {...bind("asset_owner")} data-testid="equipment-owner-input" className={inputCls} placeholder="e.g. JWA, DELPHI" /></Field>
            </div>
            <Field label="Accessories included"><textarea {...bind("accessories")} data-testid="equipment-accessories-input" className={inputCls} rows={2} /></Field>
          </Section>

          <Section title="Stock, location & status">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Quantity in stock"><input type="number" min="0" required {...bind("quantity")} data-testid="equipment-quantity-input" className={inputCls} /></Field>
              <Field label="Location">
                <select {...bind("location")} data-testid="equipment-location-select" className={inputCls}>
                  <option value="">No specific location</option>
                  {locations.map((l) => <option key={l.id} value={l.name}>{l.name}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Status">
              <select {...bind("status")} data-testid="equipment-status-select" className={inputCls}>
                {Object.entries(EQUIP_STATUS).map(([v, s]) => <option key={v} value={v}>{s.label}</option>)}
              </select>
              <p className="text-xs text-zinc-500 mt-1">
                {autoHire ? "Currently on hire — " : ""}On Hire / Off Hire update automatically from hire orders. Under Repair, Quarantined and Lost block new bookings.
              </p>
            </Field>
          </Section>

          <Section title="Certification & inspection">
            <div className="grid grid-cols-2 gap-3">
              {DATE_FIELDS.map(([k, label]) => (
                <Field key={k} label={label}><input type="date" {...bind(k)} data-testid={`equipment-${k}-input`} className={inputCls} /></Field>
              ))}
            </div>
          </Section>

          <Section title="Rates & value">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Field label="Daily rate"><input type="number" min="0" step="0.01" required {...bind("daily_rate")} data-testid="equipment-daily-input" className={inputCls} placeholder="0.00" /></Field>
              <Field label="Weekly"><input type="number" min="0" step="0.01" {...bind("weekly_rate")} data-testid="equipment-weekly-input" className={inputCls} placeholder="optional" /></Field>
              <Field label="Monthly"><input type="number" min="0" step="0.01" {...bind("monthly_rate")} data-testid="equipment-monthly-input" className={inputCls} placeholder="optional" /></Field>
              <Field label="Replacement value"><input type="number" min="0" step="0.01" {...bind("replacement_value")} data-testid="equipment-replacement-input" className={inputCls} placeholder="optional" /></Field>
            </div>
            <Field label="Revenue splits"><textarea {...bind("revenue_splits")} data-testid="equipment-revenue-splits-input" className={inputCls} rows={2} placeholder="e.g. 70% Cookes / 30% JWA" /></Field>
          </Section>

          {!editing && <p className="text-xs text-zinc-500">Damage and maintenance history can be logged from the equipment details after saving.</p>}
          <DialogFooter>
            <button type="submit" disabled={saving} data-testid="equipment-submit-button"
              className="bg-brand text-white rounded-full font-bold tracking-tight hover:bg-brand-dark transition-colors px-6 py-2 disabled:opacity-60">
              {saving ? "Saving…" : editing ? "Save changes" : "Add equipment"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
